import { useMemo, useState } from "preact/hooks"
import useSWR from "swr"
import { Link } from "wouter-preact"
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react"

import { PoItemCustomer, PoItemSummary, PoTransaction, PurchaseOrder, PurchaseOrdersResponse, fetchPurchaseOrder, fetchPurchaseOrders } from "@/api/purchase_order.tsx"
import { StatCard } from "@/components/StatCard.tsx"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { handleViewS3Uri } from "@/deduction/[id]/UserFiles.tsx"
import { PurchaseOrderFacts, PurchaseOrderLines } from "@/purchase_orders/PurchaseOrderLines.tsx"
import {
  CustomerRow,
  DUE_SOON_DAYS,
  LocationRow,
  OTHER,
  OrderFilter,
  UNPAID_AFTER_DAYS,
  chart_series,
  customer_rows,
  deducted_cents,
  deductions_of,
  due_soon,
  filter_orders,
  format_day,
  format_units,
  location_rows,
  payments_of,
  search_orders,
  ship_to_label,
  terms_label,
  to_cents,
  unpaid_orders,
  weekly_order_value,
} from "@/purchase_orders/purchase_orders_display.ts"
import { exactCurrencyFormatter, usePageTitle } from "@/utils"

const RANGES = [
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
  { days: 180, label: "Last 6 months" },
  { days: 365, label: "Last 12 months" },
]
const DEFAULT_DAYS = 90

/** Categorical slots in fixed order: a customer keeps its colour whatever else is on the chart. */
const SERIES_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4"]
const OTHER_COLOR = "#94a3b8"
const ALL_CUSTOMERS = "__all__"
const ORDER_PAGE = 100

function dollars(cents: number): string {
  return exactCurrencyFormatter(cents / 100)
}

function whole_dollars(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString("en-US")}`
}

function compact_dollars(amount: number): string {
  return amount >= 1_000_000 ? `$${(amount / 1_000_000).toFixed(1)}M` : amount >= 1000 ? `$${Math.round(amount / 1000)}k` : `$${Math.round(amount)}`
}

function percent(part: number, whole: number): string {
  if (whole <= 0) {
    return "—"
  }
  const share = (part / whole) * 100
  return part > 0 && share < 0.1 ? "<0.1%" : `${share.toFixed(1)}%`
}

function Summary({ orders, today, className }: { orders: PurchaseOrder[]; today: string; className?: string }) {
  const value = orders.reduce((sum: number, o: PurchaseOrder) => sum + to_cents(o.total_amount), 0)
  const units = orders.reduce((sum: number, o: PurchaseOrder) => sum + (o.total_quantity ?? 0), 0)
  const customers = new Set(orders.map((o: PurchaseOrder) => o.customer_name)).size
  const due = due_soon(orders, today)
  const due_value = due.reduce((sum: number, o: PurchaseOrder) => sum + to_cents(o.total_amount), 0)
  const deducted_orders = orders.filter((o: PurchaseOrder) => deductions_of(o).length > 0)
  const deducted = orders.reduce((sum: number, o: PurchaseOrder) => sum + deducted_cents(o), 0)
  const unpaid = unpaid_orders(orders, today)
  const unpaid_value = unpaid.reduce((sum: number, o: PurchaseOrder) => sum + to_cents(o.total_amount), 0)
  return (
    <div class={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 ${className ?? ""}`}>
      <StatCard title="Ordered" value={whole_dollars(value)} detail={`${orders.length.toLocaleString()} orders · ${format_units(units)} units · ${customers} customer${customers === 1 ? "" : "s"}`} />
      <StatCard title={`Due in the next ${DUE_SOON_DAYS} days`} value={String(due.length)} detail={due.length ? `${whole_dollars(due_value)} to deliver` : "Nothing due"} />
      <StatCard
        title="Deducted against these orders"
        value={whole_dollars(deducted)}
        detail={deducted_orders.length ? `${deducted_orders.length} orders · ${percent(deducted, value)} of what was ordered` : "No deduction names one of these orders"}
        warn={deducted > 0}
      />
      <StatCard
        title="No payment found"
        value={String(unpaid.length)}
        detail={unpaid.length ? `${whole_dollars(unpaid_value)} ordered ${UNPAID_AFTER_DAYS}+ days ago with no payment naming the PO` : `Every order ${UNPAID_AFTER_DAYS}+ days old has a payment, where the customer names POs`}
        warn={unpaid.length > 0}
      />
    </div>
  )
}

function WeeklyChart({ orders, className }: { orders: PurchaseOrder[]; className?: string }) {
  const series = useMemo(() => chart_series(orders), [orders])
  const rows = useMemo(() => weekly_order_value(orders, series), [orders, series])
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-base">Order value by week</CardTitle>
        <CardDescription>By the week each order was placed. A week with no bar had no orders.</CardDescription>
      </CardHeader>
      <CardContent>
        <div class="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" fontSize={11} interval="preserveStartEnd" tickLine={false} />
              <YAxis tickFormatter={compact_dollars} width={52} fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip
                formatter={(value: number, name: string) => [exactCurrencyFormatter(value), name]}
                labelFormatter={(label: string) => `Week of ${label}`}
                cursor={{ fill: "#f1f5f9" }}
              />
              {series.length > 1 && <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} formatter={(name: string) => <span class="text-slate-700">{name}</span>} />}
              {series.map((name: string, i: number) => (
                <Bar key={name} dataKey={name} stackId="value" fill={name === OTHER ? OTHER_COLOR : SERIES_COLORS[i]} stroke="#ffffff" strokeWidth={1} isAnimationActive={false} maxBarSize={36} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}

function TransactionLinks({ transactions, empty }: { transactions: PoTransaction[]; empty: string }) {
  if (transactions.length === 0) {
    return <span class="text-slate-400">{empty}</span>
  }
  return (
    <div class="flex flex-col gap-0.5">
      {transactions.map((t: PoTransaction) => (
        <div key={t.id} class="whitespace-nowrap">
          <Link href={`/deduction/${t.id}`} class="text-blue-700 hover:underline font-mono" onClick={(e: MouseEvent) => e.stopPropagation()}>
            {t.invoice_number}
          </Link>{" "}
          <span class="tabular-nums">{exactCurrencyFormatter(Math.abs(t.amount))}</span>
          {t.kind === "deduction" && t.category && <span class="text-slate-500"> · {t.category}</span>}
        </div>
      ))}
    </div>
  )
}

function OrderDetail({ id }: { id: string }) {
  const { data, error } = useSWR(["purchase-order", id], () => fetchPurchaseOrder(id), { revalidateOnFocus: false })
  if (error) {
    return <div class="text-sm text-red-600">Could not load this order.</div>
  }
  if (!data) {
    return (
      <div class="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading lines…
      </div>
    )
  }
  return (
    <div class="flex flex-col gap-3">
      <PurchaseOrderFacts order={data} />
      <PurchaseOrderLines order={data} />
      {data.s3_uri && (
        <a href="#" class="text-xs text-blue-700 hover:underline" onClick={(e: MouseEvent) => handleViewS3Uri(data.s3_uri!)(e)}>
          Open the customer's original order document
        </a>
      )}
    </div>
  )
}

function OrderRow({ order, today }: { order: PurchaseOrder; today: string }) {
  const [open, setOpen] = useState(false)
  const Chevron = open ? ChevronDown : ChevronRight
  const upcoming = order.requested_date !== null && order.requested_date >= today
  return (
    <>
      <tr class="border-b align-top hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(!open)}>
        <td class="py-2 pr-2">
          <Chevron className="h-4 w-4 text-slate-500" />
        </td>
        <td class="py-2 pr-4 font-mono whitespace-nowrap">{order.po_number}</td>
        <td class="py-2 pr-4">{order.customer_name}</td>
        <td class="py-2 pr-4 text-slate-600">{ship_to_label(order)}</td>
        <td class="py-2 pr-4 whitespace-nowrap">{format_day(order.po_date)}</td>
        <td class="py-2 pr-4 whitespace-nowrap">
          {format_day(order.requested_date)}
          {upcoming && (
            <Badge variant="outline" className="ml-2">
              Upcoming
            </Badge>
          )}
        </td>
        <td class="py-2 pr-4 text-right tabular-nums">{format_units(order.total_quantity)}</td>
        <td class="py-2 pr-4 text-right tabular-nums">{order.total_amount !== null ? exactCurrencyFormatter(order.total_amount) : "—"}</td>
        <td class="py-2 pr-4">
          <TransactionLinks transactions={deductions_of(order)} empty="—" />
        </td>
        <td class="py-2">
          <TransactionLinks transactions={payments_of(order)} empty="—" />
        </td>
      </tr>
      {open && (
        <tr class="border-b bg-slate-50/60">
          <td />
          <td colSpan={9} class="py-3 pr-4">
            <OrderDetail id={order.id} />
          </td>
        </tr>
      )}
    </>
  )
}

function OrdersTable({ orders, today }: { orders: PurchaseOrder[]; today: string }) {
  const [filter, setFilter] = useState<OrderFilter>("all")
  const [query, setQuery] = useState("")
  const [limit, setLimit] = useState(ORDER_PAGE)
  const filters: { key: OrderFilter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "deductions", label: "With deductions" },
    { key: "due_soon", label: `Due in ${DUE_SOON_DAYS} days` },
    { key: "unpaid", label: "No payment found" },
  ]
  const visible = search_orders(filter_orders(orders, filter, today), query)
  return (
    <div class="flex flex-col gap-3">
      <div class="flex items-center justify-between gap-3 flex-wrap">
        <div class="flex gap-1 flex-wrap">
          {filters.map((f: { key: OrderFilter; label: string }) => (
            <Button key={f.key} size="sm" variant={filter === f.key ? "default" : "ghost"} onClick={() => setFilter(f.key)}>
              {f.label} ({filter_orders(orders, f.key, today).length})
            </Button>
          ))}
        </div>
        <Input className="w-64" placeholder="Search PO number or ship-to" value={query} onInput={(e: Event) => setQuery((e.target as HTMLInputElement).value)} />
      </div>
      {visible.length === 0 ? (
        <div class="text-sm text-slate-500 py-6 text-center">Nothing here.</div>
      ) : (
        <div class="overflow-x-auto">
          <table class="w-full min-w-[1100px] text-sm">
            <thead>
              <tr class="text-left text-slate-500 border-b">
                <th class="py-2 pr-2" />
                <th class="py-2 pr-4 font-medium">PO</th>
                <th class="py-2 pr-4 font-medium">Customer</th>
                <th class="py-2 pr-4 font-medium">Ship to</th>
                <th class="py-2 pr-4 font-medium">Ordered</th>
                <th class="py-2 pr-4 font-medium">Requested delivery</th>
                <th class="py-2 pr-4 font-medium text-right">Units</th>
                <th class="py-2 pr-4 font-medium text-right">Value</th>
                <th class="py-2 pr-4 font-medium">Deductions</th>
                <th class="py-2 font-medium">Invoice payments</th>
              </tr>
            </thead>
            <tbody>
              {visible.slice(0, limit).map((order: PurchaseOrder) => (
                <OrderRow key={order.id} order={order} today={today} />
              ))}
            </tbody>
          </table>
          {visible.length > limit && (
            <div class="pt-3 text-center">
              <Button size="sm" variant="outline" onClick={() => setLimit(limit + ORDER_PAGE)}>
                Show {Math.min(ORDER_PAGE, visible.length - limit)} more of {visible.length}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function CustomersTable({ orders, today }: { orders: PurchaseOrder[]; today: string }) {
  const rows = customer_rows(orders, today)
  const total = rows.reduce((sum: number, r: CustomerRow) => sum + r.amount_cents, 0)
  return (
    <div class="overflow-x-auto">
      <table class="w-full min-w-[900px] text-sm">
        <thead>
          <tr class="text-left text-slate-500 border-b">
            <th class="py-2 pr-4 font-medium">Customer</th>
            <th class="py-2 pr-4 font-medium text-right">Orders</th>
            <th class="py-2 pr-4 font-medium text-right">Units</th>
            <th class="py-2 pr-4 font-medium text-right">Order value</th>
            <th class="py-2 pr-4 font-medium text-right">Share</th>
            <th class="py-2 pr-4 font-medium text-right">Avg lead time</th>
            <th class="py-2 pr-4 font-medium">Terms on the order</th>
            <th class="py-2 pr-4 font-medium text-right">Deducted</th>
            <th class="py-2 pr-4 font-medium text-right">% of orders</th>
            <th class="py-2 font-medium text-right">Orders {UNPAID_AFTER_DAYS}+ days old with a payment</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r: CustomerRow) => (
            <tr key={r.customer_name} class="border-b last:border-0">
              <td class="py-2 pr-4 font-medium">{r.customer_name}</td>
              <td class="py-2 pr-4 text-right tabular-nums">{r.orders}</td>
              <td class="py-2 pr-4 text-right tabular-nums">{format_units(r.quantity)}</td>
              <td class="py-2 pr-4 text-right tabular-nums">{dollars(r.amount_cents)}</td>
              <td class="py-2 pr-4 text-right tabular-nums">{percent(r.amount_cents, total)}</td>
              <td class="py-2 pr-4 text-right tabular-nums">{r.avg_lead_days !== null ? `${r.avg_lead_days} days` : "—"}</td>
              <td class="py-2 pr-4">{r.terms ?? "—"}</td>
              <td class="py-2 pr-4 text-right tabular-nums">{r.deducted_cents ? dollars(r.deducted_cents) : "—"}</td>
              <td class="py-2 pr-4 text-right tabular-nums">{r.deducted_cents ? percent(r.deducted_cents, r.amount_cents) : "—"}</td>
              <td class="py-2 text-right tabular-nums">{r.aged ? `${r.paid} of ${r.aged}` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ItemRow({ item, total }: { item: PoItemSummary; total: number }) {
  const [open, setOpen] = useState(false)
  const Chevron = open ? ChevronDown : ChevronRight
  const prices = item.customers.map((c: PoItemCustomer) => c.latest_price).filter((p: number | null): p is number => p !== null)
  const low = prices.length ? Math.min(...prices) : null
  const high = prices.length ? Math.max(...prices) : null
  const changed = item.customers.filter((c: PoItemCustomer) => c.min_price !== null && c.max_price !== null && c.min_price !== c.max_price)
  return (
    <>
      <tr class="border-b align-top hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(!open)}>
        <td class="py-2 pr-2">
          <Chevron className="h-4 w-4 text-slate-500" />
        </td>
        <td class="py-2 pr-4 font-mono">{item.item_number}</td>
        <td class="py-2 pr-4">{item.description ?? "—"}</td>
        <td class="py-2 pr-4 text-right tabular-nums">{format_units(item.quantity)}</td>
        <td class="py-2 pr-4 text-right tabular-nums">{exactCurrencyFormatter(item.amount)}</td>
        <td class="py-2 pr-4 text-right tabular-nums">{percent(to_cents(item.amount), total)}</td>
        <td class="py-2 pr-4 text-right tabular-nums">{item.order_count}</td>
        <td class="py-2 pr-4 text-right tabular-nums">{item.customers.length}</td>
        <td class="py-2 pr-4 text-right tabular-nums whitespace-nowrap">
          {low === null ? "—" : low === high ? exactCurrencyFormatter(low) : `${exactCurrencyFormatter(low)} – ${exactCurrencyFormatter(high!)}`}
        </td>
        <td class="py-2">{changed.length > 0 && <Badge variant="warning">Price changed at {changed.length}</Badge>}</td>
      </tr>
      {open && (
        <tr class="border-b bg-slate-50/60">
          <td />
          <td colSpan={9} class="py-2 pr-4">
            <table class="text-xs">
              <thead>
                <tr class="text-left text-slate-500">
                  <th class="py-1 pr-6 font-medium">Customer</th>
                  <th class="py-1 pr-6 font-medium text-right">Units</th>
                  <th class="py-1 pr-6 font-medium text-right">Value</th>
                  <th class="py-1 pr-6 font-medium text-right">Latest price</th>
                  <th class="py-1 font-medium text-right">Price range in period</th>
                </tr>
              </thead>
              <tbody>
                {item.customers.map((c: PoItemCustomer) => (
                  <tr key={c.customer_name}>
                    <td class="py-1 pr-6">{c.customer_name}</td>
                    <td class="py-1 pr-6 text-right tabular-nums">{format_units(c.quantity)}</td>
                    <td class="py-1 pr-6 text-right tabular-nums">{exactCurrencyFormatter(c.amount)}</td>
                    <td class="py-1 pr-6 text-right tabular-nums">{c.latest_price !== null ? exactCurrencyFormatter(c.latest_price) : "—"}</td>
                    <td class="py-1 text-right tabular-nums">
                      {c.min_price === null ? "—" : c.min_price === c.max_price ? "No change" : `${exactCurrencyFormatter(c.min_price)} – ${exactCurrencyFormatter(c.max_price!)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  )
}

function ItemsTable({ items }: { items: PoItemSummary[] }) {
  const total = items.reduce((sum: number, i: PoItemSummary) => sum + to_cents(i.amount), 0)
  return (
    <div class="overflow-x-auto">
      <table class="w-full min-w-[900px] text-sm">
        <thead>
          <tr class="text-left text-slate-500 border-b">
            <th class="py-2 pr-2" />
            <th class="py-2 pr-4 font-medium">Item</th>
            <th class="py-2 pr-4 font-medium">Description</th>
            <th class="py-2 pr-4 font-medium text-right">Units</th>
            <th class="py-2 pr-4 font-medium text-right">Order value</th>
            <th class="py-2 pr-4 font-medium text-right">Share</th>
            <th class="py-2 pr-4 font-medium text-right">Orders</th>
            <th class="py-2 pr-4 font-medium text-right">Customers</th>
            <th class="py-2 pr-4 font-medium text-right">Current price across customers</th>
            <th class="py-2" />
          </tr>
        </thead>
        <tbody>
          {items.map((item: PoItemSummary) => (
            <ItemRow key={item.item_number} item={item} total={total} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function LocationsTable({ orders }: { orders: PurchaseOrder[] }) {
  const rows = location_rows(orders)
  return (
    <div class="overflow-x-auto">
      <table class="w-full min-w-[800px] text-sm">
        <thead>
          <tr class="text-left text-slate-500 border-b">
            <th class="py-2 pr-4 font-medium">Customer</th>
            <th class="py-2 pr-4 font-medium">Ship to</th>
            <th class="py-2 pr-4 font-medium text-right">Orders</th>
            <th class="py-2 pr-4 font-medium text-right">Units</th>
            <th class="py-2 pr-4 font-medium text-right">Order value</th>
            <th class="py-2 pr-4 font-medium text-right">Orders with a deduction</th>
            <th class="py-2 pr-4 font-medium text-right">Deducted</th>
            <th class="py-2 font-medium text-right">% of orders</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r: LocationRow) => (
            <tr key={r.key} class="border-b last:border-0">
              <td class="py-2 pr-4 font-medium">{r.customer_name}</td>
              <td class="py-2 pr-4">{r.label}</td>
              <td class="py-2 pr-4 text-right tabular-nums">{r.orders}</td>
              <td class="py-2 pr-4 text-right tabular-nums">{format_units(r.quantity)}</td>
              <td class="py-2 pr-4 text-right tabular-nums">{dollars(r.amount_cents)}</td>
              <td class="py-2 pr-4 text-right tabular-nums">{r.orders_with_deductions || "—"}</td>
              <td class="py-2 pr-4 text-right tabular-nums">{r.deducted_cents ? dollars(r.deducted_cents) : "—"}</td>
              <td class="py-2 text-right tabular-nums">{r.deducted_cents ? percent(r.deducted_cents, r.amount_cents) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Body({ data }: { data: PurchaseOrdersResponse }) {
  const [customer, setCustomer] = useState(ALL_CUSTOMERS)
  const customers = useMemo(() => customer_rows(data.orders, data.to).map((r: CustomerRow) => r.customer_name), [data])
  const orders = useMemo(() => (customer === ALL_CUSTOMERS ? data.orders : data.orders.filter((o: PurchaseOrder) => o.customer_name === customer)), [data, customer])
  // Items come rolled up across every customer; narrow each to the chosen one.
  const items = useMemo(() => {
    if (customer === ALL_CUSTOMERS) {
      return data.items
    }
    return data.items
      .map((item: PoItemSummary): PoItemSummary => {
        const mine = item.customers.filter((c: PoItemCustomer) => c.customer_name === customer)
        return { ...item, customers: mine, quantity: mine.reduce((s: number, c: PoItemCustomer) => s + c.quantity, 0), amount: mine.reduce((s: number, c: PoItemCustomer) => s + to_cents(c.amount), 0) / 100 }
      })
      .filter((item: PoItemSummary) => item.customers.length > 0)
      .sort((a: PoItemSummary, b: PoItemSummary) => b.amount - a.amount)
  }, [data, customer])

  return (
    <>
      <div class="flex items-center gap-3">
        <span class="text-sm text-slate-600">Customer</span>
        <Select value={customer} onValueChange={setCustomer}>
          <SelectTrigger className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_CUSTOMERS}>All customers</SelectItem>
            {customers.map((name: string) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Summary orders={orders} today={data.to} />
      <WeeklyChart orders={orders} />
      <Card>
        <CardContent className="pt-6">
          <Tabs defaultValue="orders">
            <TabsList>
              <TabsTrigger value="orders">Orders ({orders.length})</TabsTrigger>
              <TabsTrigger value="customers">Customers</TabsTrigger>
              <TabsTrigger value="items">Items ({items.length})</TabsTrigger>
              <TabsTrigger value="locations">Ship-to locations</TabsTrigger>
            </TabsList>
            <TabsContent value="orders" className="pt-4">
              <OrdersTable orders={orders} today={data.to} />
            </TabsContent>
            <TabsContent value="customers" className="pt-4">
              <CustomersTable orders={orders} today={data.to} />
            </TabsContent>
            <TabsContent value="items" className="pt-4">
              <ItemsTable items={items} />
            </TabsContent>
            <TabsContent value="locations" className="pt-4">
              <LocationsTable orders={orders} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </>
  )
}

export function PurchaseOrdersPage() {
  usePageTitle("Purchase orders")
  const [days, setDays] = useState(DEFAULT_DAYS)
  const { data, error, isLoading, isValidating } = useSWR(["purchase-orders", days], () => fetchPurchaseOrders(days), { revalidateOnFocus: false, keepPreviousData: true })
  // Orders on file may not reach back as far as the window asks for; say so rather than let a
  // longer window look like it did nothing.
  const short_history = data?.earliest_po_date && data.earliest_po_date > data.from ? data.earliest_po_date : null

  return (
    <div class="p-6 flex flex-col gap-6 w-full">
      <div class="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 class="text-2xl font-semibold">Purchase orders</h1>
          <p class="text-slate-600 mt-1 max-w-3xl">
            Every order your customers sent by EDI: what they ordered, at what price, where it ships and when it is due. Each order shows the
            deductions and invoice payments that name its PO number, so a shortage or pricing claim can be checked against the order itself.
          </p>
          {data && (
            <p class="text-sm text-slate-500 mt-2">
              Orders placed {format_day(data.from)} – {format_day(data.to)}
              {short_history && <> · the earliest order on file is {format_day(short_history)}, so a longer window shows nothing more</>}
            </p>
          )}
        </div>
        <div class="flex items-center gap-2">
          {isValidating && data && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          <Select value={String(days)} onValueChange={(v: string) => setDays(Number(v))}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGES.map((r: { days: number; label: string }) => (
              <SelectItem key={r.days} value={String(r.days)}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading && !data ? (
        <div class="flex items-center gap-2 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading purchase orders…
        </div>
      ) : error || !data ? (
        <div class="text-red-600">Could not load purchase orders.</div>
      ) : data.orders.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-slate-500">
            No purchase orders in this period. Orders appear here when your EDI provider mails them to a mailbox connected to MarginWiz.
          </CardContent>
        </Card>
      ) : (
        <Body data={data} />
      )}
    </div>
  )
}
